import React, { useEffect, useState } from "react";
import {
    Alert,
    Card,
    Placeholder,
    Table,
} from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import NoResult from "@core/components/NoResult";
import PaginationBar from "@core/components/PaginationBar";
import TablePaginationFooter, {
    getPaginationRange,
} from "@core/components/TablePaginationFooter";
import TableActionButton from "@core/components/TableActionButton";
import TableActions from "@core/components/TableActions";
import StatusPill from "@core/components/StatusPill";
import EntityCard from "@core/components/EntityCard";
import { FiEdit2, FiSlash, FiCheckCircle } from "react-icons/fi";
import { SearchBar, FilterField } from "@core/components/SearchBar";
import {
    NAME_SORT_FILTER_FIELD,
    SORT_BY_SUB_GROUP_NAME,
    SORT_DIR_ASC,
    countContentFilters,
    filtersWithoutSort,
    normalizeSortDir,
    readSortDirFromSearchParams,
    withCatalogSort,
} from "@core/constants/listSort";
import { useDebounce } from "@core/hooks/useDebounce";
import { useSearchUrlSync } from "@core/hooks/useSearchUrlSync";
import { useScopeId } from "@core/hooks/useRouteParamId";
import { useReportSubGroups } from "@features/report/subgroup/hooks/useReportSubGroups";
import { useReportSubGroupActions } from "@features/report/subgroup/hooks/useReportSubGroupActions";
import { ReportSubGroup } from "@features/report/subgroup/types/reportSubGroup";
import UpdateSubGroupModal from "@features/report/subgroup/components/UpdateSubGroupModal";
import StatusChangeConfirmModal, { StatusChangeKind } from "@features/admin/finance/components/StatusChangeConfirmModal";
import { usePermissions } from "@features/entity/hooks/usePermissions";
import { ACTION_CODES } from "@features/entity/types/actionCodes";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { ErrorResponse } from "@core/types/errorResponse";

const SUB_GROUP_STATUS_ACTION_CODES = [ACTION_CODES.ENABLE_REPORT_SUB_GROUP, ACTION_CODES.DISABLE_REPORT_SUB_GROUP];

const REPORT_SUB_GROUP_FILTER_FIELDS: FilterField[] = [
    NAME_SORT_FILTER_FIELD,
    {
        name: "subGroupName",
        label: "Sub Group Name",
        type: "text",
        placeholder: "Filter by sub group name",
        colWidth: 6,
    },
    {
        name: "description",
        label: "Description",
        type: "text",
        placeholder: "Filter by description",
        colWidth: 6,
    },
    {
        name: "isActive",
        label: "Status",
        type: "select",
        options: [{ value: "1", label: "Active" }, { value: "0", label: "Inactive" }],
        colWidth: 6,
    },
];

const ReportSubGroupTable: React.FC<{
    nationId?: number;
    showPagination?: boolean;
    refreshToken?: number;
}> = ({ nationId, showPagination = false, refreshToken }) => {
    const [searchParams] = useSearchParams();
    const scopeId = useScopeId();
    const resolvedNationId = nationId ?? scopeId ?? 0;

    const [page, setPage] = useState(() => Number(searchParams.get("page")) || 1);
    const [searchQuery, setSearchQuery] = useState(() => searchParams.get("q") ?? "");
    const [activeFilters, setActiveFilters] = useState<Record<string, any>>(() => {
        const filters: Record<string, any> = {};
        REPORT_SUB_GROUP_FILTER_FIELDS.forEach(({ name }) => {
            const value = searchParams.get(name);
            if (value) filters[name] = value;
        });
        filters.sortDir = readSortDirFromSearchParams(searchParams);
        return filters;
    });
    const debouncedQuery = useDebounce(searchQuery, 300);

    useSearchUrlSync({ query: debouncedQuery, filters: activeFilters, page });

    const [selectedSubGroup, setSelectedSubGroup] = useState<ReportSubGroup | null>(null);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [statusConfirm, setStatusConfirm] = useState<{ subGroup: ReportSubGroup; kind: StatusChangeKind } | null>(null);

    const showError = useApiError();
    const { busy: statusBusy, doSubGroupDisable, doSubGroupEnable } = useReportSubGroupActions();

    // ADMIN-only enable/disable gated on NATION-scoped action codes.
    const { hasPermission } = usePermissions({
        scopeType: "NATION",
        scopeId: resolvedNationId || undefined,
        actionCodes: SUB_GROUP_STATUS_ACTION_CODES,
    });
    const canDisable = hasPermission(ACTION_CODES.DISABLE_REPORT_SUB_GROUP);
    const canEnable = hasPermission(ACTION_CODES.ENABLE_REPORT_SUB_GROUP);

    const appliedFilters = Object.fromEntries(
        Object.entries(filtersWithoutSort(activeFilters)).filter(([, value]) => value !== "" && value != null)
    );
    const activeFilterCount = countContentFilters(activeFilters);
    const hasSearch = !!(debouncedQuery || activeFilterCount);

    const {
        current_page,
        data,
        from,
        last_page,
        per_page,
        total,
        loading,
        reload,
    } = useReportSubGroups(
        withCatalogSort(
            {
                nationId: resolvedNationId,
                page,
                ...(!hasSearch ? { all: true } : {}),
                freetext: debouncedQuery || undefined,
                ...appliedFilters,
                sortDir: normalizeSortDir(activeFilters.sortDir),
            },
            SORT_BY_SUB_GROUP_NAME
        )
    );

    useEffect(() => {
        if (refreshToken === undefined) return;
        void reload();
    }, [refreshToken, reload]);

    const handleStatusConfirm = async () => {
        if (!statusConfirm || !resolvedNationId) return;
        const { subGroup, kind } = statusConfirm;
        const response = kind === "disable"
            ? await doSubGroupDisable(resolvedNationId, subGroup.report_sub_group_id)
            : await doSubGroupEnable(resolvedNationId, subGroup.report_sub_group_id);

        if (response.isSuccess()) {
            showSuccess(response.getData()?.message ?? `Report sub group ${kind}d successfully.`);
            setStatusConfirm(null);
            void reload();
        } else {
            showError(response.getError() as ErrorResponse);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / (per_page ?? 20)));
    const pageNumber = current_page ?? page;
    const { start, end } = getPaginationRange({
        from,
        page: pageNumber,
        perPage: per_page,
        total,
        currentCount: data.length,
    });

    if (!Number.isFinite(resolvedNationId) || resolvedNationId <= 0) {
        return (
            <Alert variant="danger" className="my-4 shadow-sm">
                Error: Missing required parameter. <strong>nationId</strong>.
            </Alert>
        );
    }

    // Shared by the desktop table row and the mobile card.
    const renderSubGroupActions = (subGroup: ReportSubGroup) => (
        <TableActions aria-label={`Actions for sub group ${subGroup.name}`}>
            <TableActionButton
                icon={<FiEdit2 aria-hidden="true" />}
                label="Edit"
                primary
                onClick={() => {
                    setSelectedSubGroup(subGroup);
                    setShowUpdateModal(true);
                }}
                data-testid={`edit-sub-group-button-${subGroup.report_sub_group_id}`}
            />
            {subGroup.is_active && canDisable && (
                <TableActionButton
                    icon={<FiSlash />}
                    label="Disable"
                    variant="outline-secondary"
                    className="ntcog-table-action-btn--danger"
                    onClick={() => setStatusConfirm({ subGroup, kind: "disable" })}
                    data-testid={`disable-sub-group-button-${subGroup.report_sub_group_id}`}
                />
            )}
            {!subGroup.is_active && canEnable && (
                <TableActionButton
                    icon={<FiCheckCircle />}
                    label="Enable"
                    variant="outline-secondary"
                    className="ntcog-table-action-btn--success"
                    onClick={() => setStatusConfirm({ subGroup, kind: "enable" })}
                    data-testid={`enable-sub-group-button-${subGroup.report_sub_group_id}`}
                />
            )}
        </TableActions>
    );

    return (
        <div>
            <div className="ntcog-list-search-section">
                <SearchBar
                    placeholder="Search report sub groups..."
                    filterFields={REPORT_SUB_GROUP_FILTER_FIELDS}
                    initialQuery={searchQuery}
                    initialFilters={activeFilters}
                    activeFilterCount={activeFilterCount}
                    loading={loading}
                    onQueryChange={(query) => {
                        setSearchQuery(query);
                        setPage(1);
                    }}
                    onFiltersApply={(filters) => {
                        setActiveFilters({ ...filters, sortDir: normalizeSortDir(filters.sortDir) });
                        setPage(1);
                    }}
                    onClear={() => {
                        setSearchQuery("");
                        setActiveFilters({ sortDir: SORT_DIR_ASC });
                        setPage(1);
                    }}
                    data-testid="report-sub-group-search-bar"
                />
            </div>

            <Card className="shadow-sm ntcog-card ntcog-list-table-card">
                <Card.Body className="p-0">
                    <div className="px-3 pb-3 d-none d-md-block">
                        <Table responsive
                            hover
                            size="sm"
                            className="mb-0 align-middle table-striped ntcog-table"
                            data-testid="report-sub-group-table"
                        >
                            <thead className="table-light">
                                <tr className="small text-muted">
                                    <th>#</th>
                                    <th>Name</th>
                                    <th>Description</th>
                                    <th>Status</th>
                                    <th className="text-end" style={{ width: 160 }}>
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="table-group-divider">
                                {loading &&
                                    Array.from({ length: 6 }).map((_, index) => (
                                        <tr key={`sub-group-ph-${index}`}>
                                            <td className="text-muted">
                                                <Placeholder xs={6} />
                                            </td>
                                            <td className="text-muted">
                                                <Placeholder xs={6} />
                                            </td>
                                            <td className="text-muted">
                                                <Placeholder xs={8} />
                                            </td>
                                            <td className="text-muted">
                                                <Placeholder xs={4} />
                                            </td>
                                            <td className="text-end">
                                                <Placeholder xs={3} />
                                            </td>
                                        </tr>
                                    ))}

                                {!loading && data.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="py-5">
                                            <NoResult
                                                line_1="No sub groups found."
                                                line_2="Try adjusting the filters or create a new report sub group."
                                            />
                                        </td>
                                    </tr>
                                )}

                                {!loading &&
                                    data.map((subGroup, index) => {
                                        const rowNumber = from != null ? from + index : index + 1;

                                        return (
                                            <tr key={subGroup.report_sub_group_id}>
                                                <td>{rowNumber}</td>
                                                <td>{subGroup.name}</td>
                                                <td>{subGroup.description || "—"}</td>
                                                <td><StatusPill active={!!subGroup.is_active} /></td>
                                                <td className="text-end">
                                                    {renderSubGroupActions(subGroup)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </Table>
                    </div>

                    {/* Mobile (< md): stacked cards */}
                    <div className="ntcog-card-list d-md-none" data-testid="report-sub-group-cards">
                        {loading && Array.from({ length: 6 }).map((_, index) => (
                            <div key={`sub-group-ph-card-${index}`} className="ntcog-card-list__item">
                                <EntityCard
                                    testId={`sub-group-card-skeleton-${index}`}
                                    title={<Placeholder as="span" animation="glow"><Placeholder xs={6} /></Placeholder>}
                                    description={<Placeholder as="span" animation="glow"><Placeholder xs={8} /></Placeholder>}
                                />
                            </div>
                        ))}
                        {!loading && data.length === 0 && (
                            <NoResult
                                line_1="No sub groups found."
                                line_2="Try adjusting the filters or create a new report sub group."
                            />
                        )}
                        {!loading && data.map((subGroup) => (
                            <div key={subGroup.report_sub_group_id} className="ntcog-card-list__item">
                                <EntityCard
                                    testId={`sub-group-card-${subGroup.report_sub_group_id}`}
                                    ariaLabel={`Sub group ${subGroup.name}`}
                                    title={<span className="fw-semibold">{subGroup.name}</span>}
                                    status={<StatusPill active={!!subGroup.is_active} />}
                                    actions={renderSubGroupActions(subGroup)}
                                    description={subGroup.description || undefined}
                                />
                            </div>
                        ))}
                    </div>
                </Card.Body>
            </Card>

            {showPagination && (
                <TablePaginationFooter
                    className="mt-3"
                    testId="pagination-bar"
                    info={`Showing ${start} to ${end} of ${total} report sub groups`}
                >
                    <PaginationBar
                        page={pageNumber}
                        totalPages={last_page ?? totalPages}
                        onPage={(nextPage) => setPage(nextPage)}
                    />
                </TablePaginationFooter>
            )}

            <UpdateSubGroupModal
                show={showUpdateModal}
                onHide={() => setShowUpdateModal(false)}
                subGroup={selectedSubGroup}
                onUpdated={() => {
                    void reload();
                }}
            />

            <StatusChangeConfirmModal
                show={!!statusConfirm}
                kind={statusConfirm?.kind ?? "disable"}
                entityLabel="report sub group"
                entityName={statusConfirm?.subGroup.name ?? ""}
                busy={statusBusy}
                onConfirm={handleStatusConfirm}
                onCancel={() => setStatusConfirm(null)}
                bodyText={statusConfirm?.kind === "disable" ? (
                    <>
                        Are you sure you want to disable <strong>{statusConfirm?.subGroup.name}</strong>? It can no longer be assigned to report templates. Existing assignments are unaffected.
                    </>
                ) : undefined}
            />
        </div>
    );
};

export default ReportSubGroupTable;
